/**
 * El panel de un dominio, en una ventana.
 *
 * Se abre al pulsar un dominio en Facturación y trae lo que alguien va a buscar
 * al panel de su registrador: en qué estado está, cuándo vence, su DNS y cómo
 * llevárselo. Antes había que salir del panel para cualquiera de las cuatro.
 *
 * ── Por qué una ventana y no una sección ────────────────────────────────────
 *
 * Porque se entra a hacer UNA cosa y se sale. Un dominio no se administra: se
 * mira de vez en cuando y se toca casi nunca. Una sección con su dirección
 * propia habría añadido un sitio más al menú para algo que se visita dos veces
 * al año.
 *
 * ── Lo que NO está, y por qué ───────────────────────────────────────────────
 *
 * Nameservers, registros glue y DNSSEC. Porkbun los tiene porque su público son
 * registradores y gente de sistemas; aquí, cambiar los nameservers es la forma
 * más rápida de que alguien tire su propio sitio sin entender qué pasó. Quien de
 * verdad los necesita sabe pedir el traslado del dominio.
 */
import { useCallback, useEffect, useState } from 'react'
import { longDate } from '../../lib/dates'
import { useI18n, translateServerError } from '../../lib/i18n'
import {
  IconAlertTriangle,
  IconCheck,
  IconClock,
  IconClockCheck,
  IconKey,
  IconCopy,
  IconLoader2,
  IconLock,
  IconPencil,
  IconPlus,
  IconShieldCheck,
  IconTrash,
  IconWorld,
  IconX,
} from '@tabler/icons-react'
import { createDnsRecord, deleteDnsRecord, getAuthCodeRequest, listDnsRecords, requestAuthCode, updateDnsRecord, type AuthCodeRequest, type BillingDomainCharge, type DnsInput, type DnsRecord, type DnsRecordType } from '../../lib/api'

const TYPES: DnsRecordType[] = ['A', 'AAAA', 'CNAME', 'MX', 'TXT']

/**
 * Qué hace cada tipo, dicho para quien no sabe qué es un registro DNS.
 *
 * Es la diferencia entre un desplegable que se elige a ciegas y uno que se
 * entiende. «CNAME» no significa nada; «apunta un subdominio a otra dirección»
 * sí, y es exactamente lo que alguien viene a hacer.
 */
/* Las tres tablas de aquí guardan CLAVES, no texto: son constantes de módulo y
   ahí no se puede llamar a un hook. Quien las usa las pasa por su `t`. */
const TYPE_HELP: Record<DnsRecordType, string> = {
  A: 'domain.type.A',
  AAAA: 'domain.type.AAAA',
  CNAME: 'domain.type.CNAME',
  MX: 'domain.type.MX',
  TXT: 'domain.type.TXT',
}

/** Cuántos días quedan. Negativo si ya pasó. */
function daysUntil(iso: string): number {
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000)
}

/**
 * El estado del certificado, en palabras.
 *
 * Enseñaba el valor crudo de Cloudflare —`pending_validation`— en la ficha de
 * un dominio. Es el nombre interno de un estado de una API de terceros: no
 * significa nada para quien lo lee y encima parece un error.
 */
const SSL_LABEL: Record<string, string> = {
  active: 'domain.ssl.active',
  pending_validation: 'domain.ssl.pending_validation',
  pending_issuance: 'domain.ssl.pending_issuance',
  pending_deployment: 'domain.ssl.pending_deployment',
  pending_deletion: 'domain.ssl.pending_deletion',
  expired: 'domain.ssl.expired',
  deleted: 'domain.ssl.deleted',
  initializing: 'domain.ssl.initializing',
}

const STATUS: Record<string, { label: string; tone: 'ok' | 'warn' | 'error' }> = {
  active: { label: 'domain.status.active', tone: 'ok' },
  verifying: { label: 'domain.status.verifying', tone: 'warn' },
  pending_dns: { label: 'domain.status.pending_dns', tone: 'warn' },
  purchasing: { label: 'domain.status.purchasing', tone: 'warn' },
  disconnected: { label: 'domain.status.disconnected', tone: 'warn' },
  error: { label: 'domain.status.error', tone: 'error' },
}

type Draft = DnsInput & { id?: string }

const EMPTY: Draft = { type: 'CNAME', name: '', content: '', ttl: 600 }

export function DomainSettingsModal({ domain, token, onClose }: {
  domain: BillingDomainCharge
  token: string
  onClose: () => void
}) {
  const { t } = useI18n()
  const [records, setRecords] = useState<DnsRecord[] | null>(null)
  const [failed, setFailed] = useState('')
  const [draft, setDraft] = useState<Draft | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [confirmDelete, setConfirmDelete] = useState<DnsRecord | null>(null)
  const [copied, setCopied] = useState(false)
  /* `undefined` mientras no se sabe; `null` = nunca se pidió. La diferencia
     importa: sin ella, la ventana enseñaría «pídelo» durante medio segundo a
     alguien que ya lo pidió ayer. */
  const [authRequest, setAuthRequest] = useState<AuthCodeRequest | null | undefined>(undefined)
  const [asking, setAsking] = useState(false)
  const [codeCopied, setCodeCopied] = useState(false)

  const load = useCallback(async () => {
    setFailed('')
    try {
      const data = await listDnsRecords(domain.projectId, domain.id, token)
      setRecords(data.records)
    } catch (err) {
      setFailed(translateServerError(err, t('err.readDns')))
      setRecords([])
    }
  }, [domain.projectId, domain.id, token, t])

  useEffect(() => { void load() }, [load])

  useEffect(() => {
    let alive = true
    getAuthCodeRequest(token, domain.projectId, domain.id)
      .then((value) => { if (alive) setAuthRequest(value) })
      /* Sin ruido: si no se sabe, se ofrece pedirlo. Pedir dos veces no duplica
         nada —el servidor devuelve la solicitud viva— así que equivocarse por
         este lado no cuesta nada. */
      .catch(() => { if (alive) setAuthRequest(null) })
    return () => { alive = false }
  }, [token, domain.projectId, domain.id])

  const askForCode = async () => {
    if (asking) return
    setAsking(true)
    setError('')
    try {
      setAuthRequest(await requestAuthCode(token, domain.projectId, domain.id))
    } catch (err) {
      setError(translateServerError(err, t('err.registerRequest')))
    } finally {
      setAsking(false)
    }
  }

  /* Escape cierra, como cualquier ventana del panel. Se cierra sólo el
     formulario si hay uno abierto: perder lo escrito por pulsar Escape una vez
     de más es la clase de cosa que hace desconfiar de un editor. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      if (draft) setDraft(null)
      else onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => { window.removeEventListener('keydown', onKey) }
  }, [draft, onClose])

  const save = async () => {
    if (!draft) return
    setBusy(true)
    setError('')
    try {
      const body: DnsInput = {
        type: draft.type,
        name: draft.name.trim(),
        content: draft.content.trim(),
        ttl: draft.ttl ?? 600,
        ...(draft.type === 'MX' ? { prio: draft.prio ?? 10 } : {}),
      }
      if (draft.id) await updateDnsRecord(domain.projectId, domain.id, draft.id, body, token)
      else await createDnsRecord(domain.projectId, domain.id, body, token)
      setDraft(null)
      await load()
    } catch (err) {
      setError(translateServerError(err, t('err.saveRecord')))
    } finally {
      setBusy(false)
    }
  }

  const remove = async (record: DnsRecord) => {
    setBusy(true)
    setError('')
    try {
      await deleteDnsRecord(domain.projectId, domain.id, record.id, token)
      setConfirmDelete(null)
      await load()
    } catch (err) {
      setError(translateServerError(err, t('err.deleteRecord')))
    } finally {
      setBusy(false)
    }
  }

  const copyHostname = () => {
    void navigator.clipboard?.writeText(domain.hostname)
    setCopied(true)
    window.setTimeout(() => { setCopied(false) }, 1800)
  }

  /* Sin fila, el estado crudo del servidor: es preferible a un hueco, y dice
     qué buscar en el registro. No pasa por `t` porque no es una clave. */
  const known = STATUS[domain.status]
  const status = known
    ? { label: t(known.label), tone: known.tone }
    : { label: domain.status, tone: 'warn' as const }
  const days = domain.renewsAt ? daysUntil(domain.renewsAt) : null
  const sslReady = domain.sslStatus === 'active'
  const transferAvailableAt = new Date(
    new Date(domain.purchasedAt).getTime() + 60 * 86_400_000,
  ).toISOString()
  const transferLocked = daysUntil(domain.purchasedAt) > -60

  return (
    <div
      className="dash-modal-backdrop dash-domain-modal-backdrop"
      role="presentation"
      onClick={(event) => { if (event.target === event.currentTarget) onClose() }}
    >
      <div className="dash-domain-modal" role="dialog" aria-modal="true" aria-label={t('accountDomains.settingsOf', { hostname: domain.hostname })}>

        <header className="dash-domain-modal-head">
          <span className="dash-widget-glass-icon dash-settings-head-icon" aria-hidden="true">
            <IconWorld size={20} />
          </span>
          <div className="dash-domain-modal-title">
            <h2>{domain.hostname}</h2>
            <p>{domain.projectName}</p>
          </div>
          <span className={`dash-connect-status is-${status.tone === 'ok' ? 'ok' : status.tone === 'error' ? 'error' : 'warn'}`}>
            {status.tone === 'ok' ? <IconShieldCheck size={14} /> : <IconAlertTriangle size={14} />}
            {status.label}
          </span>
          <button type="button" className="dash-icon-btn" aria-label={t('common.close')} onClick={onClose}>
            <IconX size={16} />
          </button>
        </header>

        <div className="dash-domain-modal-body">

          {/* ── Resumen ────────────────────────────────────────────────── */}
          <section className="dash-domain-facts">
            <div>
              <dt>{t('domain.certificate')}</dt>
              <dd className={sslReady ? 'is-ok' : ''}>
                {t(sslReady ? 'domain.ssl.active' : SSL_LABEL[domain.sslStatus ?? ''] ?? 'domain.ssl.pending_issuance')}
              </dd>
            </div>
            <div>
              <dt>{t('domain.expires')}</dt>
              <dd>
                {domain.renewsAt ? longDate(domain.renewsAt) : '—'}
                {/* Los días sólo cuando falta poco: a diez meses vista no es un
                    dato, es ruido. A menos de sesenta empieza a importar. */}
                {days !== null && days <= 60 ? (
                  <small className={days <= 15 ? 'is-warn' : ''}>
                    {days > 0
                      ? t(days === 1 ? 'domain.inDaysOne' : 'domain.inDaysMany', { count: days })
                      : t('domain.expired')}
                  </small>
                ) : null}
              </dd>
            </div>
            <div>
              <dt>{t('domain.registeredOn')}</dt>
              <dd>{longDate(domain.purchasedAt)}</dd>
            </div>
            <div>
              <dt>{t('domain.domain')}</dt>
              <dd>
                <button type="button" className="dash-domain-copy" onClick={copyHostname}>
                  {copied
                    ? <><IconCheck size={13} /> {t('domain.copied')}</>
                    : <><IconCopy size={13} /> {t('domain.copy')}</>}
                </button>
              </dd>
            </div>
          </section>

          {/* ── DNS ────────────────────────────────────────────────────── */}
          <section className="dash-domain-section">
            <div className="dash-domain-section-head">
              <div>
                <h3>{t('domain.dns')}</h3>
                <p>{t('domain.dnsBody')}</p>
              </div>
              <button
                type="button"
                className="dash-secondary-btn"
                disabled={records === null || Boolean(draft)}
                onClick={() => { setDraft({ ...EMPTY }); setError('') }}
              >
                <IconPlus size={14} /> {t('domain.addRecord')}
              </button>
            </div>

            {draft ? (
              <form
                className="dash-domain-form"
                onSubmit={(event) => { event.preventDefault(); void save() }}
              >
                <label className="dash-domain-field is-type">
                  <span>{t('domain.field.type')}</span>
                  <select
                    value={draft.type}
                    disabled={busy}
                    onChange={(event) => { setDraft({ ...draft, type: event.target.value as DnsRecordType }) }}
                  >
                    {TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                </label>

                <label className="dash-domain-field">
                  <span>{t('domain.field.name')}</span>
                  <input
                    value={draft.name}
                    disabled={busy}
                    placeholder="www"
                    onChange={(event) => { setDraft({ ...draft, name: event.target.value }) }}
                  />
                  <small>{t('domain.emptyMeans', { hostname: domain.hostname })}</small>
                </label>

                <label className="dash-domain-field is-wide">
                  <span>{t('domain.field.value')}</span>
                  <input
                    value={draft.content}
                    disabled={busy}
                    placeholder={draft.type === 'MX'
                      ? t('domain.valuePlaceholderMx')
                      /* La IP de ejemplo no se traduce: es un número reservado
                         para documentación (RFC 5737), igual en todos lados. */
                      : draft.type === 'A' ? '203.0.113.10' : t('domain.valuePlaceholderCname')}
                    onChange={(event) => { setDraft({ ...draft, content: event.target.value }) }}
                  />
                </label>

                {draft.type === 'MX' ? (
                  <label className="dash-domain-field is-narrow">
                    <span>{t('domain.field.priority')}</span>
                    <input
                      type="number"
                      min={0}
                      value={draft.prio ?? 10}
                      disabled={busy}
                      onChange={(event) => { setDraft({ ...draft, prio: Number(event.target.value) }) }}
                    />
                  </label>
                ) : null}

                <p className="dash-domain-type-help">{t(TYPE_HELP[draft.type])}</p>

                <div className="dash-domain-form-actions">
                  <button type="button" className="dash-ghost-btn" disabled={busy} onClick={() => { setDraft(null) }}>
                    {t('common.cancel')}
                  </button>
                  <button type="submit" className="dash-primary-btn" disabled={busy || !draft.content.trim()}>
                    {busy ? <IconLoader2 size={14} className="dash-spin" /> : <IconCheck size={14} />}
                    {t(draft.id ? 'domain.saveChanges' : 'domain.add')}
                  </button>
                </div>
              </form>
            ) : null}

            {error ? <p className="dash-domain-error" role="alert">{error}</p> : null}

            {records === null ? (
              <p className="dash-card-description">{t('domain.loadingDns')}</p>
            ) : failed ? (
              /* Sólo la frase. Antes se pintaba además el cuerpo entero del
                 error dentro de un `<code>`, y con un fallo del registrador eso
                 eran veinte líneas de volcado de pila con rutas del worker
                 dentro de la ventana del dominio. El detalle técnico ya va al
                 registro del servidor, que es donde sirve. */
              <div className="dash-inline-msg">
                <span>{failed}</span>
                <div className="dash-inline-msg-actions">
                  <button type="button" className="dash-secondary-btn" onClick={() => { void load() }}>{t('domain.retry')}</button>
                </div>
              </div>
            ) : records.length === 0 ? (
              <p className="dash-card-description">{t('domain.noRecords')}</p>
            ) : (
              <div className="dash-domain-table-scroll">
                <table className="dash-domain-table">
                  <thead>
                    <tr>
                      <th>{t('domain.field.type')}</th>
                      <th>{t('domain.field.name')}</th>
                      <th>{t('domain.field.value')}</th>
                      <th>{t('domain.field.ttl')}</th>
                      <th aria-label={t('domain.actions')} />
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((record) => (
                      <tr key={record.id} className={record.locked ? 'is-locked' : ''}>
                        <td><span className="dash-domain-type">{record.type}</span></td>
                        <td className="dash-domain-name">{record.name}</td>
                        <td className="dash-domain-value" title={record.content}>{record.content}</td>
                        <td className="dash-domain-ttl">{record.ttl}</td>
                        <td className="dash-domain-row-actions">
                          {record.locked ? (
                            /* Bloqueado y dicho, no escondido: un registro que
                               desaparece de la lista y reaparece solo es más
                               desconcertante que uno con un candado y su
                               explicación al pasar por encima. */
                            <span
                              className="dash-domain-lock"
                              data-tooltip={t('domain.lockedTip')}
                            >
                              <IconLock size={14} />
                            </span>
                          ) : (
                            <>
                              <button
                                type="button"
                                className="dash-icon-btn"
                                aria-label={t('domain.editRecord', { type: record.type, name: record.name })}
                                disabled={busy}
                                onClick={() => {
                                  setError('')
                                  setDraft({
                                    id: record.id,
                                    type: (TYPES.includes(record.type as DnsRecordType) ? record.type : 'TXT') as DnsRecordType,
                                    name: record.shortName,
                                    content: record.content,
                                    ttl: record.ttl,
                                    ...(record.prio !== null ? { prio: record.prio } : {}),
                                  })
                                }}
                              >
                                <IconPencil size={14} />
                              </button>
                              <button
                                type="button"
                                className="dash-icon-btn is-danger"
                                aria-label={t('domain.deleteRecord', { type: record.type, name: record.name })}
                                disabled={busy}
                                onClick={() => { setError(''); setConfirmDelete(record) }}
                              >
                                <IconTrash size={14} />
                              </button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* ── Llevárselo ─────────────────────────────────────────────── */}
          <section className="dash-domain-section">
            <div className="dash-domain-section-head">
              <div>
                <h3>{t('domain.transfer')}</h3>
              </div>
            </div>

            <div className="dash-domain-transfer">
              {authRequest?.status === 'pending' ? (
                <div className="dash-domain-requested">
                  <IconClockCheck size={16} aria-hidden="true" />
                  <span>
                    <strong>{t('domain.codeRequested')}</strong>
                    <small>{t('domain.codeRequestedBody')}</small>
                  </span>
                </div>
              ) : authRequest?.status === 'sent' && authRequest.code ? (
                /* El código, aquí mismo. También se manda por correo: el correo
                   llega solo —quien lo pidió no está mirando la pantalla— y esto
                   sigue estando cuando el correo se pierde. Uno de los dos
                   siempre falla. */
                <div className="dash-domain-code">
                  <p className="dash-domain-code-label">
                    <IconKey size={15} aria-hidden="true" />
                    {t('domain.yourCode')}
                  </p>
                  <div className="dash-domain-code-value">
                    <code>{authRequest.code}</code>
                    <button
                      type="button"
                      className="dash-secondary-btn"
                      onClick={() => {
                        void navigator.clipboard?.writeText(authRequest.code ?? '')
                        setCodeCopied(true)
                        window.setTimeout(() => { setCodeCopied(false) }, 1800)
                      }}
                    >
                      {codeCopied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      {t(codeCopied ? 'domain.copied' : 'domain.copy')}
                    </button>
                  </div>
                  <small>
                    {t('domain.codeHelp')}
                    {authRequest.codeExpiresAt
                      ? t('domain.codeKept', { date: longDate(authRequest.codeExpiresAt) })
                      : ''}
                  </small>
                </div>
              ) : authRequest?.status === 'sent' ? (
                <div className="dash-domain-requested is-done">
                  <IconCheck size={16} aria-hidden="true" />
                  <span>
                    <strong>{t('domain.codeSent')}</strong>
                    <small>{t('domain.codeSentBody')}</small>
                  </span>
                </div>
              ) : (
                <button
                  type="button"
                  className="dash-secondary-btn"
                  disabled={asking || authRequest === undefined}
                  onClick={() => { void askForCode() }}
                >
                  {asking ? <IconLoader2 size={14} className="dash-spin" /> : null}
                  {t(asking ? 'domain.asking' : 'domain.askCode')}
                </button>
              )}

              {authRequest ? (
                <div className="dash-domain-transfer-details">
                  <p>
                    <IconLock size={15} aria-hidden="true" />
                    <span>{t('domain.unlockNote')}</span>
                  </p>
                  {transferLocked ? (
                    <p className="dash-domain-icann">
                      <IconClock size={15} aria-hidden="true" />
                      <span>
                        <strong>{t('domain.transferableFrom', { date: longDate(transferAvailableAt) })}</strong>
                        <small>{t('domain.icann')}</small>
                      </span>
                    </p>
                  ) : null}
                </div>
              ) : null}
            </div>
          </section>

        </div>

        {confirmDelete ? (
          <div className="dash-domain-confirm" role="alertdialog" aria-label={t('domain.confirmDeleteAria')}>
            <div>
              <strong>{t('domain.confirmDelete')}</strong>
              <p>
                <span className="dash-domain-type">{confirmDelete.type}</span> {confirmDelete.name} → {confirmDelete.content}
              </p>
              <p className="dash-domain-confirm-note">{t('domain.confirmDeleteNote')}</p>
              <div className="dash-domain-form-actions">
                <button type="button" className="dash-ghost-btn" disabled={busy} onClick={() => { setConfirmDelete(null) }}>
                  {t('common.cancel')}
                </button>
                <button type="button" className="dash-primary-btn is-danger" disabled={busy} onClick={() => { void remove(confirmDelete) }}>
                  {busy ? <IconLoader2 size={14} className="dash-spin" /> : <IconTrash size={14} />}
                  {t('domain.delete')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

      </div>
    </div>
  )
}
